import type { AdSlot, Dictionary } from '@ad-engine/core';
import { payload, props } from 'ts-action';
import { EventOptions } from './events/event-options';

export interface GeneralPayload {
	name: string;
	state: string;
}

export interface AdIntervention {
	id: string;
	message: string;
	slotName: string;
}

export interface CcpaSignalPayload {
	ccpaSignal: boolean;
	geoRequiresSignal: boolean;
}

export interface GdprConsentPayload {
	gdprConsent: boolean;
	geoRequiresConsent: boolean;
}

export interface LoadTemplatePayload {
	slotName: string;
	type: string;
}

export interface TrackingBidDefinition {
	bidderName: string;
	buyerId?: string;
	price: string;
	responseTimestamp: number;
	slotName: string;
	size: string;
	timeToRespond: number;
	additionalInfo?: {
		dsa?: object;
	};
}

export interface UapLoadStatus {
	isLoaded: boolean;
	adProduct: string;
}

export interface BiddersEventPayload {
	provider: string;
	slotName: string;
}

export interface AdSlotEventPayload {
	event: string;
	slot: AdSlot;
	adSlotName: string;
	status: string;
	payload?: any;
}

export interface IdentityDataPayload {
	partnerName: string;
	partnerIdentityId: string | null;
}

export interface VideoAdsOptions {
	showAds: boolean;
	targetingParams?: string;
	videoAdUnitPath?: string;
}

export const eventsRepository = {
	// Integrated partners events //
	IDENTITY_PARTNER_DATA_OBTAINED: {
		name: 'Identity partner data obtained',
		payload: payload<IdentityDataPayload>(),
	},
	PARTNER_LOAD_STATUS: {
		name: 'Partner load status',
		payload: props<{ status: string }>(),
	},
	// Integrated partners events //
	ANYCLIP_READY: {
		name: 'Anyclip ready',
	},
	ANYCLIP_START: {
		name: 'Anyclip start',
	},
	ANYCLIP_LATE_INJECT: {
		name: 'Anyclip late inject',
	},
	SYSTEM1_STARTED: {
		name: 'System1 started',
	},
	NO_NATIVE_PREBID_AD: {
		name: 'No native prebid ad',
		payload: props<{ slotName: string }>(),
	},
	INTENT_IQ_GROUP_OBTAINED: {
		name: 'IntentIQ A/B test group obtained',
		payload: props<{ abTestGroup: string }>(),
	},
	NO_NATIVO_AD: {
		name: 'No nativo ad',
		payload: props<{ slotName: string }>(),
	},
	LIVE_CONNECT_STARTED: {
		name: 'LiveConnect started',
	},
	LIVE_CONNECT_CACHED: {
		name: 'LiveConnect data cached',
	},
	LIVE_CONNECT_RESPONDED_UUID: {
		name: 'LiveConnect responded with UUID',
	},
	LIVE_CONNECT_LOADED: {
		name: 'LiveConnect loaded',
	},
	LIVE_CONNECT_HEM_READY: {
		name: 'LiveConnect HEM ready',
		payload: props<{ unifiedId: string }>(),
	},
	YAHOO_STARTED: {
		name: 'Yahoo started',
	},
	IDENTITY_ENGINE_READY: {
		category: '[IdentityEngine]',
		name: 'Identity ready',
	},

	// Platforms events //
	F2_HIDE_SMART_BANNER: {
		category: '[AdEngine F2 Templates]',
		name: 'hide smart banner',
	},
	FAN_FEED_READY: {
		category: '[FanFeed]',
		name: 'Ready',
	},
	QUIZ_AD_INJECTED: {
		category: '[quizConsumption]',
		name: 'ad slot injected',
		payload: props<{ slotId: string }>(),
	},
	RAIL_READY: {
		category: '[Rail]',
		name: 'Ready',
	},
	// Remove after ADEN-14313 is complete
	MOBILE_MORE_ICB_EXPERIMENT: {
		name: 'Mobile More ICB Experiment event',
	},
	STICKY_RAIL_DESKTOP_MULTI_ICB_READY: {
		name: 'Sticky Rail Desktop Multi ICB Ready',
		payload: props<{ repeatAmount: number }>(),
	},
} as const satisfies Dictionary<EventOptions>;
